// ─────────────────────────────────────────────────────────────────────────────
// uiSlice.ts — Redux slice for UI / framework-switching state
//
// Controls which front-end pattern (MVC / MVVM / Flux / Atomic) and which
// back-end architecture are currently active. Components read from this slice
// to know what to render / which API base URL to call.
// ─────────────────────────────────────────────────────────────────────────────

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type {
  FrontEndPattern,
  FrontEndFramework,
  BackEndArchitecture,
  CVLayout,
} from '@/types/cv.types';

/** Port numbers for each back-end — change to match your local setup */
export const BACKEND_PORTS: Record<BackEndArchitecture, number> = {
  'nlayer':             5000,
  'clean-architecture': 5001,
  'nodejs':             5002,
  'hexagonal':          5003,
  'vertical-slice':     5004,
};

/** Production API subdomains — must match docker/Caddyfile and DNS records */
export const BACKEND_HOSTS: Record<BackEndArchitecture, string> = {
  'nlayer':             'api-nlayer.johncronogue.com',
  'clean-architecture': 'api-clean.johncronogue.com',
  'nodejs':             'api-node.johncronogue.com',
  'hexagonal':          'api-hex.johncronogue.com',
  'vertical-slice':     'api-vslice.johncronogue.com',
};

interface UIState {
  /** Currently active front-end rendering pattern */
  frontEndPattern: FrontEndPattern;
  /** Currently active front-end framework (used for nav links) */
  frontEndFramework: FrontEndFramework;
  /** Currently active back-end architecture */
  backEndArchitecture: BackEndArchitecture;
  /** Which mobile bottom-sheet (if any) is currently open */
  mobileSheet: 'pages' | 'customize' | 'more' | null;
  /** ID of the currently expanded pattern demo on the Patterns page */
  activePatternId: string | null;
  /** Active CV page layout style */
  cvLayout: CVLayout;
  /** Active colour scheme ID */
  colourScheme: string;
}

const initialState: UIState = {
  frontEndPattern: 'flux',
  frontEndFramework: 'react-redux',
  backEndArchitecture: 'nlayer',
  mobileSheet: null,
  activePatternId: null,
  cvLayout: 'executive',
  colourScheme: 'hologram',
};

const uiSlice = createSlice({
  name: 'ui',
  initialState,

  reducers: {
    /**
     * setFrontEndPattern — Changes the rendering pattern for the CV page.
     * The CV data itself doesn't change — only how it's wired to the view.
     */
    setFrontEndPattern: (state, action: PayloadAction<FrontEndPattern>) => {
      state.frontEndPattern = action.payload;
    },

    /**
     * setFrontEndFramework — Updates the navbar to reflect which framework
     * is active. Navigating to another framework opens its separate bundle.
     */
    setFrontEndFramework: (state, action: PayloadAction<FrontEndFramework>) => {
      state.frontEndFramework = action.payload;
    },

    /**
     * setBackEndArchitecture — Switches the API target.
     * After this fires, the CV slice should be reset so components re-fetch
     * from the newly selected back-end.
     */
    setBackEndArchitecture: (state, action: PayloadAction<BackEndArchitecture>) => {
      state.backEndArchitecture = action.payload;
    },

    /** openMobileSheet — Opens one of the mobile bottom sheets (Pages / Customize / More) */
    openMobileSheet: (state, action: PayloadAction<'pages' | 'customize' | 'more'>) => {
      state.mobileSheet = action.payload;
    },

    /** closeMobileSheet — Explicitly close (e.g. on route change, backdrop tap) */
    closeMobileSheet: (state) => {
      state.mobileSheet = null;
    },

    /** setActivePattern — Expands a pattern card on the Patterns page */
    setActivePattern: (state, action: PayloadAction<string | null>) => {
      state.activePatternId = action.payload;
    },

    /** setCVLayout — Changes the visual layout of the CV page */
    setCVLayout: (state, action: PayloadAction<CVLayout>) => {
      state.cvLayout = action.payload;
    },

    /** setColourScheme — Changes the active colour scheme */
    setColourScheme: (state, action: PayloadAction<string>) => {
      state.colourScheme = action.payload;
    },
  },
});

export const {
  setFrontEndPattern,
  setFrontEndFramework,
  setBackEndArchitecture,
  openMobileSheet,
  closeMobileSheet,
  setActivePattern,
  setCVLayout,
  setColourScheme,
} = uiSlice.actions;

export default uiSlice.reducer;
